import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Users, Sparkles, Leaf, Check, ArrowRight } from 'lucide-react';

const servicesData = {
  weddings: {
    icon: Heart,
    title: 'Wedding Ceremonies',
    tagline: 'Celebrate Your Forever',
    description: 'Traditional and contemporary wedding ceremonies crafted to honor your unique love story. From intimate gatherings to grand celebrations, every ceremony is personalized to reflect who you are as a couple.',
    features: [
      'Personalized ceremony script',
      'Vow writing assistance',
      'Rehearsal coordination',
      'Unity ceremony options',
      'Cultural & religious traditions',
      'Same-day coordination',
    ],
    process: [
      'Initial consultation to understand your vision',
      'Custom ceremony design and script writing',
      'Rehearsal walkthrough',
      'Ceremony day coordination and officiation',
    ],
  },
  'vow-renewals': {
    icon: Sparkles,
    title: 'Vow Renewal Ceremonies',
    tagline: 'Reaffirm Your Love',
    description: 'Celebrate your continued commitment with a meaningful vow renewal ceremony. Whether marking a milestone anniversary or simply wanting to reaffirm your love, we create beautiful moments of reconnection.',
    features: [
      'Milestone anniversary celebrations',
      'Reaffirmation ceremonies',
      'Intimate family gatherings',
      'Updated vow assistance',
      'Memory incorporation',
      'Flexible locations',
    ],
    process: [
      'Consultation about your journey together',
      'Ceremony design reflecting your history',
      'New vow creation or original vow incorporation',
      'Beautiful ceremony celebrating your continued love',
    ],
  },
  'commitment-ceremonies': {
    icon: Users,
    title: 'Commitment Ceremonies',
    tagline: 'Honor Your Bond',
    description: 'Honoring partnerships with ceremonies that reflect your values, beliefs, and the unique bond you share. All couples and all forms of commitment are celebrated with equal reverence and joy.',
    features: [
      'Inclusive of all relationships',
      'Customized rituals and readings',
      'Non-denominational options',
      'Family blessing ceremonies',
      'Ring or symbol exchanges',
      'All faiths and beliefs welcome',
    ],
    process: [
      'Understanding your relationship and values',
      'Creating meaningful rituals and readings',
      'Designing symbols of your commitment',
      'Officiating a ceremony true to your bond',
    ],
  },
  elopements: {
    icon: Leaf,
    title: 'Elopement Ceremonies',
    tagline: 'Intimate & Meaningful',
    description: 'For couples seeking an intimate, private exchange of vows. Elopements strip away the complexity and focus on what matters most—your love and commitment to each other.',
    features: [
      'Flexible location options',
      'Quick turnaround available',
      'Witness services if needed',
      'Simple or elaborate options',
      'Adventure elopements',
      'Weekday availability',
    ],
    process: [
      'Brief consultation about your vision',
      'Location and timing coordination',
      'Simple but meaningful ceremony design',
      'Private, intimate ceremony officiation',
    ],
  },
};

const packages = [
  {
    name: 'Essential',
    price: 'Starting at $350',
    description: 'Perfect for intimate ceremonies and elopements',
    features: [
      'Initial consultation',
      'Standard ceremony script',
      'Day-of officiation',
      'Marriage license signing',
    ],
    highlighted: false,
  },
  {
    name: 'Signature',
    price: 'Starting at $550',
    description: 'Our most popular package for full ceremonies',
    features: [
      'Everything in Essential',
      'Custom ceremony script',
      'Vow writing guidance',
      'Rehearsal attendance',
      'Unity ceremony coordination',
      'Up to 30 miles travel',
    ],
    highlighted: true,
  },
  {
    name: 'Premium',
    price: 'Starting at $850',
    description: 'Complete ceremony experience with all the extras',
    features: [
      'Everything in Signature',
      'Multiple consultations',
      'Day-of coordination support',
      'Extended rehearsal time',
      'Unlimited travel (within state)',
      'Rush booking available',
      'Backup officiant on call',
    ],
    highlighted: false,
  },
];

const Services = () => {
  const { serviceType } = useParams();
  const service = serviceType ? servicesData[serviceType] : null;

  // If viewing a specific service
  if (service) {
    const IconComponent = service.icon;
    return (
      <div className="pt-20" data-testid={`service-page-${serviceType}`}>
        {/* Hero */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-brand-moody/30 to-brand-charcoal" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 rounded-full bg-gold-500/10 flex items-center justify-center mx-auto mb-6">
              <IconComponent className="w-10 h-10 text-gold-500" />
            </div>
            <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
              {service.tagline}
            </p>
            <h1 className="text-4xl md:text-5xl font-serif text-warm-100 mb-6">
              {service.title}
            </h1>
            <div className="divider-gold max-w-xs mx-auto" />
            <p className="text-warm-100/70 text-lg max-w-2xl mx-auto mt-6">
              {service.description}
            </p>
          </div>
        </section>

        {/* Features & Process */}
        <section className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Features */}
            <div>
              <h2 className="text-2xl font-serif text-warm-100 mb-6">
                What's Included
              </h2>
              <ul className="space-y-4">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="w-5 h-5 text-gold-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-warm-100/80">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Process */}
            <div>
              <h2 className="text-2xl font-serif text-warm-100 mb-6">
                Our Process
              </h2>
              <ol className="space-y-6">
                {service.process.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-8 h-8 rounded-full bg-gold-500 text-brand-charcoal flex items-center justify-center text-sm font-bold mr-4 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-warm-100/80 pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link
              to="/booking"
              className="btn-gold text-sm tracking-[0.2em] uppercase"
            >
              Book This Service
            </Link>
          </div>
        </section>

        {/* Back to all services */}
        <div className="text-center pb-16">
          <Link
            to="/services"
            className="text-gold-500 hover:text-gold-400 text-sm tracking-wider uppercase transition-colors"
          >
            ← View All Services & Packages
          </Link>
        </div>
      </div>
    );
  }

  // Main services page
  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-moody/30 to-brand-charcoal" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
            Sacred Services
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-warm-100 mb-6">
            Ceremonies & Packages
          </h1>
          <div className="divider-gold max-w-xs mx-auto" />
          <p className="text-warm-100/70 text-lg max-w-2xl mx-auto mt-6">
            Every ceremony is thoughtfully crafted to honor your unique love story.
            Choose a service that speaks to your journey.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-container">
        <h2 className="text-3xl font-serif text-warm-100 text-center mb-12">
          Our Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {Object.entries(servicesData).map(([key, service]) => {
            const IconComponent = service.icon;
            return (
              <Link
                key={key}
                to={`/services/${key}`}
                className="card-elegant p-8 group hover:border-gold-500/40 transition-all"
              >
                <div className="w-16 h-16 rounded-full bg-gold-500/10 flex items-center justify-center mb-6 group-hover:bg-gold-500/20 transition-colors">
                  <IconComponent className="w-8 h-8 text-gold-500" />
                </div>
                <h3 className="text-2xl font-serif text-warm-100 mb-2">
                  {service.title}
                </h3>
                <p className="text-gold-500/80 text-sm mb-4">{service.tagline}</p>
                <p className="text-warm-100/60 mb-4 line-clamp-2">
                  {service.description}
                </p>
                <span className="text-gold-500 text-sm tracking-wider uppercase flex items-center gap-2 group-hover:gap-3 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 bg-brand-moody/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
              Investment
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-warm-100 mb-6">
              Service Packages
            </h2>
            <div className="divider-gold max-w-xs mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`card-elegant p-8 relative ${
                  pkg.highlighted ? 'border-gold-500/50 scale-105' : ''
                }`}
                data-testid={`package-${pkg.name.toLowerCase()}`}
              >
                {pkg.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gold-500 text-brand-charcoal px-4 py-1 text-xs tracking-wider uppercase rounded-sm">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-serif text-warm-100 mb-2">
                  {pkg.name}
                </h3>
                <p className="text-gold-500 text-xl font-semibold mb-4">
                  {pkg.price}
                </p>
                <p className="text-warm-100/60 text-sm mb-6">
                  {pkg.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <Check className="w-4 h-4 text-gold-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-warm-100/70">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/booking"
                  className={`block text-center text-sm tracking-wider uppercase py-3 rounded-sm transition-colors ${
                    pkg.highlighted
                      ? 'btn-gold'
                      : 'btn-gold-outline'
                  }`}
                >
                  Select Package
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-warm-100/50 text-sm mt-8">
            Custom packages available. Contact us to discuss your specific needs.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Services;
